import React, { useState, useEffect } from 'react';
import MainContent from './MainContent';
import SidebarMail from './SidebarMail';
import useApi from '../../../../utils/api-manager/Helper/useApi';

const MailApp = () => {
  const [activeTab, setActiveTab] = useState('inbox');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [emails, setEmails] = useState({ inbox: [], sent: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { Get } = useApi();

  const safeDate = (input) => {
    const date = new Date(input);
    return isNaN(date.getTime()) ? new Date().toISOString() : input;
  };

  // Load emails from localStorage on component mount
  useEffect(() => {
    const storedEmails = localStorage.getItem('emails');
    if (storedEmails) {
      setEmails(JSON.parse(storedEmails));
    }
  }, []);

  // Save emails to localStorage whenever emails state changes
  useEffect(() => {
    localStorage.setItem('emails', JSON.stringify(emails));
  }, [emails]);

  // Fetch inbox emails when inbox tab is active
  useEffect(() => {
    if (activeTab !== 'inbox') return;

    const fetchInboxEmails = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await Get('readEmail');
        if (!Array.isArray(response)) {
          throw new Error('Invalid response format: expected an array');
        }

        const inboxEmails = response.map((email, index) => ({
          id: index + 1,
          sender: email.from || "Unknown",
          subject: email.subject || "(No Subject)",
          body: email.body || "",
          date: safeDate(email.date || new Date().toISOString()),
          read: false,
        }));

        setEmails(prev => {
          const updatedEmails = { ...prev, inbox: inboxEmails };
          localStorage.setItem('emails', JSON.stringify(updatedEmails));
          return updatedEmails;
        });

      } catch (err) {
        console.error("Error fetching inbox emails:", err);
        setError(err.message);
        setEmails(prev => ({ ...prev, inbox: [] }));
      } finally {
        setLoading(false);
      }
    };

    fetchInboxEmails();
  }, [activeTab]);

  // Fetch sent emails when sent tab is active - FIXED VERSION
  useEffect(() => {
    if (activeTab !== 'sent') return;

    const fetchSentEmails = async () => {
      setLoading(true);
      setError(null);
      try {
        const imapResponse = await Get('sentEmailsImap');

        let imapSentEmails = [];

        // Assuming Get returns the data directly
        if (Array.isArray(imapResponse.sent_emails)) {
          imapSentEmails = imapResponse.sent_emails.map((email, index) => ({
            id: email.id || `imap-${index}`,
            sender: email.from || "You",
            recipient: email.to || "Unknown",
            subject: email.subject || "(No Subject)",
            body: email.body || "",
            date: safeDate(email.date),
            read: true,
          }));
        }

        setEmails(prev => ({ ...prev, sent: imapSentEmails }));
      } catch (err) {
        console.error("Error fetching sent emails from IMAP:", err);
        setError(err.message);
        setEmails(prev => ({ ...prev, sent: [] }));
      } finally {
        setLoading(false);
      }
    };

    // Always fetch sent emails when switching to sent tab
    fetchSentEmails();
  }, [activeTab]);

  const handleEmailSent = (sentEmail) => {
    setEmails(prev => ({
      ...prev,
      sent: [sentEmail, ...prev.sent]
    }));
  };

  const currentEmails = emails[activeTab] || [];
  const filteredEmails = currentEmails.filter(email =>
    (email.subject || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (email.sender || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (email.recipient || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleEmailClick = (email) => {
    setSelectedEmail(email);
    if (!email.read) {
      // Create a new object to trigger re-render
      const updatedEmails = emails[activeTab].map(e => 
        e.id === email.id ? { ...e, read: true } : e
      );
      setEmails(prev => ({ ...prev, [activeTab]: updatedEmails }));
    }
  };

  return (
    <div style={{ display: 'flex', height: '100vh', fontFamily: 'Arial, sans-serif', backgroundColor: '#f5f5f5' }}>
      <SidebarMail
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        sentCount={emails.sent.length}
      />

      <MainContent
        activeTab={activeTab}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filteredEmails={filteredEmails}
        selectedEmail={selectedEmail}
        handleEmailClick={handleEmailClick}
        onEmailSent={handleEmailSent}
        loading={loading}
        error={error}
      />
    </div>
  );
};

export default MailApp;